import { useIdleTimer } from 'react-idle-timer';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Honeybadger } from '@honeybadger-io/react';
import { useQueryClient } from '@tanstack/react-query';

import { requestApi } from 'api/Api';
import { kioskIdleRequest, kioskPaymentStop } from 'api/Kiosk';

import { isKioskDevice } from 'lib/helpers/kiosk';

import { useIsKioskSaleArmed } from '../lib/useKioskSaleArmed';
import {
  SALE_PAUSE_MAX_SECONDS,
  WARNING_SECONDS,
  endsPatientSession,
  isHandoffScreen,
  isTermsScreen,
  resolveIdleReset,
  resolveIdleSeconds,
  resolveWarningMessage
} from '../lib/idleScreens';

import IdleCountdownOverlay from './IdleCountdownOverlay';

// Single, app-wide guard (mounted once in App.jsx). It decides whether the
// current screen belongs to a shared/kiosk device that must auto-logout on
// inactivity, so individual screens never have to opt in. The sign-in screens are
// the exception: they mount their own clear-in-place AutoIdle.
const AutoIdleWarper = () => {
  const { pathname } = useLocation();

  // `myriadIsKiosk` marks a shared device (physical kiosk or kiosk tablet) and is never set on
  // a patient's personal device, so personal instant links, own-device check-in and a patient
  // paying their own invoice link all stay excluded.
  const isKiosk = isKioskDevice();
  const inScope =
    pathname.startsWith('/kiosk/') ||
    pathname.startsWith('/mobile/') ||
    (isKiosk && (isHandoffScreen(pathname) || isTermsScreen(pathname)));

  // Never run on the attract/idle/destination screens (prevents redirect loops).
  if (!inScope || pathname === '/kiosk' || pathname.includes('idle')) return null;

  return <AutoIdle resetTo={resolveIdleReset(pathname)} />;
};

// `onExpire` switches to clear-in-place: the countdown runs the callback instead of leaving
// the screen, and the practice isn't told (there is no patient to report on).
export const AutoIdle = ({ resetTo = '/kiosk', onExpire, disabled = false }) => {
  const { pathname } = useLocation();

  const [remaining, setRemaining] = useState(WARNING_SECONDS);
  const [isWarning, setIsWarning] = useState(false);
  const [isSalePauseCapped, setIsSalePauseCapped] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isSaleArmed = useIsKioskSaleArmed();
  const isPausedForSale = isSaleArmed && !isSalePauseCapped;
  const clearsInPlace = !!onExpire;

  // The countdown runs from a setInterval that closes over the render it started on, so
  // without this a route change mid-countdown would log out (or skip it) for the screen the
  // patient already left.
  const idleScreenRef = useRef({ pathname, resetTo, onExpire, isSaleArmed });
  idleScreenRef.current = { pathname, resetTo, onExpire, isSaleArmed };

  const notifyPractice = (status) => {
    if (clearsInPlace) return;
    kioskIdleRequest(navigate, { status, url: idleScreenRef.current.pathname });
  };

  const dismissWarning = () => {
    setIsWarning(false);
    setRemaining(WARNING_SECONDS);
  };

  const { reset } = useIdleTimer({
    timeout: resolveIdleSeconds(pathname) * 1000,
    // Disabling pauses the timer; re-enabling starts a fresh full timeout.
    disabled: disabled || isPausedForSale,
    onIdle: () => {
      setRemaining(WARNING_SECONDS);
      setIsWarning(true);
      notifyPractice('idle');
    },
    onActive: () => {
      dismissWarning();
      notifyPractice('active');
    },
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
      'visibilitychange'
    ]
  });

  // A sale armed mid-countdown (a pushed payment landing) takes over: the patient is
  // walking to the reader, so the countdown is withdrawn rather than run out.
  useEffect(() => {
    if (!isSaleArmed) {
      setIsSalePauseCapped(false);
      return undefined;
    }

    if (isWarning) notifyPractice('active');
    dismissWarning();
    const capTimer = setTimeout(() => setIsSalePauseCapped(true), SALE_PAUSE_MAX_SECONDS * 1000);
    return () => clearTimeout(capTimer);
  }, [isSaleArmed]);

  const handleStillHere = () => {
    dismissWarning();
    reset();
  };

  // On the handoff and check-in screens the patient is signed in, so dropping the session
  // is the only thing that keeps the next patient from resuming this one's check-in.
  // The storage/React Query purge is owned by the idle screen we land on (views/Idle.jsx),
  // so it deliberately isn't repeated here.
  const expireSession = async () => {
    const {
      pathname: idlePath,
      resetTo: idleResetTo,
      onExpire: expireInPlace,
      isSaleArmed: isSaleStillArmed
    } = idleScreenRef.current;

    if (expireInPlace) {
      expireInPlace();
      return;
    }

    try {
      // Only reachable once the sale pause cap has run out. Leaving without cancelling would
      // strand the reader mid-charge, and it has to go before logout drops the session.
      if (isSaleStillArmed) await kioskPaymentStop(navigate, {});

      await queryClient.cancelQueries();
      await kioskIdleRequest(navigate, { status: 'timeout', url: idlePath });
      if (endsPatientSession(idlePath)) {
        await requestApi({ url: '/api/user/logout', params: {}, navigate });
      }
    } catch (error) {
      Honeybadger.notify(error, '[error]AutoIdle:expireSession');
    } finally {
      // requestApi never throws, so every outcome lands here - the patient must leave the
      // screen either way.
      navigate(idleResetTo);
    }
  };

  useEffect(() => {
    if (!isWarning) return undefined;

    let secondsLeft = WARNING_SECONDS;
    setRemaining(secondsLeft);

    const interval = setInterval(() => {
      secondsLeft -= 1;
      setRemaining(secondsLeft);

      if (secondsLeft <= 0) {
        clearInterval(interval);
        dismissWarning();
        reset();
        expireSession();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isWarning]);

  return (
    <IdleCountdownOverlay
      isOpen={isWarning}
      remaining={remaining}
      total={WARNING_SECONDS}
      message={resolveWarningMessage({ pathname, remaining, clearsInPlace })}
      onStay={handleStillHere}
    />
  );
};

export default AutoIdleWarper;
